import { useEffect, useRef, useContext } from 'react';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useNotifications } from '@/contexts/NotificationsContext';
import { IncomingTransferContext } from '@/contexts/IncomingTransferContext';
import { useSettings } from '@/contexts/SettingsContext';
import { subscribeToTransferNotifications, updateNotificationStatus } from '@/services/transferNotificationService';
import { verifyTransactionOnChain } from '@/utils/verifyTransactionOnChain';

/**
 * Hook que escuta notificações de transferências no Firestore para a carteira conectada.
 *
 * Fluxo:
 * - Inscreve em `transferNotifications` (status: pending, to == walletAddress).
 * - Para cada nova notificação, verifica a transação na blockchain.
 * - Se confirmada, cria uma notificação local (NotificationsContext) e marca como `verified` no Firestore.
 */
/** Evita spam de log quando o erro é de permissão (ex.: regras do Firestore incorretas). */
function isFirestorePermissionError(err) {
  const msg = err?.message ?? String(err);
  return msg.includes('Missing or insufficient permissions') || msg.includes('PERMISSION_DENIED');
}

export function useTransferNotificationListener() {
  const { walletAddress, selectedNetwork } = useWalletConnection();
  const { addNotification } = useNotifications();
  const { reportIncomingTransfer } = useContext(IncomingTransferContext) || {};
  const { enableFirestoreNotifications, enableOnChainVerificationForNotifications } = useSettings();
  const permissionErrorLoggedRef = useRef(false);

  useEffect(() => {
    if (!walletAddress) return;
    if (!enableFirestoreNotifications) return;

    permissionErrorLoggedRef.current = false;

    const unsubscribe = subscribeToTransferNotifications(walletAddress, async (notification) => {
      try {
        if (enableOnChainVerificationForNotifications) {
          const isConfirmed = await verifyTransactionOnChain(notification.txHash, notification.network);
          if (!isConfirmed) return;
        }

        const amountNum = Number(notification.amount ?? 0);
        const payload = {
          type: 'incoming_funds',
          txHash: notification.txHash,
          amount: amountNum,
          symbol: notification.token,
          networkName: notification.networkName || selectedNetwork?.name || '',
          chainId: notification.network || selectedNetwork?.chainId || '',
          fromAddress: notification.from,
        };

        // Notificação no sino (lista de notificações)
        addNotification(payload);

        // Fecha o modal/inline do QR para receber e abre o popup de recebimento (se a tela de Receber estiver aberta)
        if (typeof reportIncomingTransfer === 'function') {
          reportIncomingTransfer({
            amount: amountNum,
            symbol: notification.token,
            txHash: notification.txHash,
            networkName: notification.networkName || selectedNetwork?.name || '',
            chainId: notification.network || selectedNetwork?.chainId || '',
            tokenAddress: notification.tokenAddress ?? '',
            fromAddress: notification.from,
            receivedAt: Date.now(),
          });
        }

        // Atualiza status no Firestore
        if (notification.id) {
          await updateNotificationStatus(notification.id, 'verified');
        }
      } catch (err) {
        if (isFirestorePermissionError(err)) {
          if (!permissionErrorLoggedRef.current) {
            permissionErrorLoggedRef.current = true;
            console.error(
              '[useTransferNotificationListener] Permissão Firestore negada. Confira as regras de transferNotifications (firestore.rules).',
              err,
            );
          }
        } else {
          console.error('[useTransferNotificationListener] Erro ao processar notificação:', err);
        }
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [
    walletAddress,
    selectedNetwork?.name,
    selectedNetwork?.chainId,
    addNotification,
    reportIncomingTransfer,
    enableFirestoreNotifications,
    enableOnChainVerificationForNotifications,
  ]);
}

